import { useEffect, useState } from "react";
import { deleteCoffreItem, listCoffre, readCoffreText, type CoffreEntry } from "./coffre";
import { isNotesFile } from "./notesPad";
import type { CrmFiche } from "./types";

type PagePick = { path: string; label: string };

function noteHasText(note: string): boolean {
  return note.replace(/\n*<!--BLOCKS_JSON[\s\S]*$/, "").trim() !== "";
}

function pageTitle(name: string): string {
  return name.replace(/\.md$/i, "");
}

function isFichePage(name: string): boolean {
  const lower = name.trim().toLowerCase();
  if (!lower.endsWith(".md") || lower.endsWith(".board.md")) return false;
  return !isNotesFile(name);
}

async function listFichePages(root: string, ownerId: number): Promise<PagePick[]> {
  const found: PagePick[] = [];
  const seen = new Set<string>();
  async function walk(path: string, prefix: string, depth: number) {
    if (depth > 8) return;
    let rows: CoffreEntry[] = [];
    try {
      rows = await listCoffre(path, false, ownerId, true);
    } catch (error) {
      if (depth === 0) throw error;
      return;
    }
    const folders: CoffreEntry[] = [];
    rows.forEach((row) => {
      const name = String(row.name || "");
      const kind = String(row.kind || "");
      if (kind === "folder" || kind === "dir") {
        folders.push(row);
        return;
      }
      if (!isFichePage(name) || seen.has(row.path)) return;
      seen.add(row.path);
      const title = pageTitle(name);
      found.push({ path: row.path, label: prefix ? `${prefix} / ${title}` : title });
    });
    for (const folder of folders) {
      const folderName = String(folder.name || "");
      await walk(folder.path, prefix ? `${prefix} / ${folderName}` : folderName, depth + 1);
    }
  }
  await walk(root, "", 0);
  return found.sort((a, b) => a.label.localeCompare(b.label, "fr"));
}

export function FichePagePicker({ fiche, note, onUse }: { fiche: CrmFiche | null | undefined; note: string; onUse: (content: string) => void }) {
  const ownerId = fiche?.coffre_owner_id || 0;
  const root = fiche?.coffre_path || "";
  const [pages, setPages] = useState<PagePick[]>([]);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ask, setAsk] = useState<"copy" | "take" | null>(null);

  useEffect(() => {
    if (!root) {
      setPages([]);
      setPicked("");
      return;
    }
    let alive = true;
    setError("");
    void listFichePages(root, ownerId)
      .then((rows) => {
        if (!alive) return;
        setPages(rows);
        setPicked((current) => (rows.some((row) => row.path === current) ? current : ""));
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Pages illisibles.");
      });
    return () => {
      alive = false;
    };
  }, [root, ownerId]);

  async function apply(mode: "copy" | "take", path = picked) {
    const page = pages.find((row) => row.path === path);
    if (!page) return;
    setBusy(true);
    setError("");
    try {
      const raw = await readCoffreText(page.path, ownerId);
      if (mode === "take") await deleteCoffreItem(page.path, ownerId);
      onUse(raw);
      setAsk(null);
      if (mode === "take") {
        setPages((rows) => rows.filter((row) => row.path !== page.path));
        setPicked("");
      }
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Transfert impossible.");
    } finally {
      setBusy(false);
    }
  }

  function request(mode: "copy" | "take", path: string) {
    setPicked(path);
    if (mode === "copy" && !noteHasText(note)) {
      void apply(mode, path);
      return;
    }
    setAsk(mode);
  }

  if (!fiche || !root || pages.length === 0) return error ? <p className="mb-2 text-xs text-rose-700">{error}</p> : null;
  const selected = pages.find((row) => row.path === picked);
  const askText = ask === "take"
    ? `Récupérer « ${selected?.label || "cette page"} » ? Son contenu remplace le bloc-note, et la page part dans la corbeille du Coffre.`
    : `Copier « ${selected?.label || "cette page"} » ? Le contenu actuel du bloc-note est remplacé. La page reste dans la fiche.`;

  return (
    <div className="mb-2 grid gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Pages de la fiche</p>
      <ul className="grid max-h-36 gap-1 overflow-auto">
        {pages.map((page) => (
          <li key={page.path} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1">
            <span className="min-w-0 flex-1 truncate text-sm">{page.label}</span>
            <button type="button" className="shrink-0 text-[11px] font-semibold text-slate-500 disabled:opacity-40" disabled={busy} onClick={() => request("copy", page.path)}>Copier</button>
            <button type="button" className="shrink-0 text-[11px] font-semibold text-slate-500 disabled:opacity-40" disabled={busy} onClick={() => request("take", page.path)}>Récupérer</button>
          </li>
        ))}
      </ul>
      {ask ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-950">
          <p>{askText}</p>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold" onClick={() => setAsk(null)}>Annuler</button>
            <button type="button" className="rounded-full bg-rose-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void apply(ask)}>{ask === "take" ? "Récupérer" : "Copier"}</button>
          </div>
        </div>
      ) : null}
      {error ? <p className="text-xs text-rose-700">{error}</p> : null}
    </div>
  );
}
